/**
 * Custom API Error class to attach response details.
 */
class APIError extends Error {
  response: Response;
  responseBody: any | null;

  constructor(message: string, response: Response, responseBody: any | null) {
    super(message);
    this.name = "APIError";
    this.response = response;
    this.responseBody = responseBody;
  }
}

/**
 * Supported HTTP methods
 */
type HTTPMethod = "GET" | "POST" | "PUT" | "DELETE";

/**
 * Fetch from the app server with error handling and JSON parsing.
 * 
 * @param url - API endpoint URL.
 * @param method - HTTP method (GET, POST, etc.).
 * @param access_token - Optional Bearer token for authentication.
 * @param body - Optional request payload.
 * @returns Parsed JSON response of type T.
 */
async function appServerFetch<T>(
  url: string,
  method: HTTPMethod,
  access_token?: string,
  body?: Record<string, any>
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (access_token) {
    headers["Authorization"] = `Bearer ${access_token}`;
  }

  if (body) {
    headers["Content-Type"] = "application/json";
  }

  const options: RequestInit = {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  };

  const response = await fetch(url, options);

  if (response.status !== 200) {
    let responseBody = null;
    try {
      responseBody = await response.json();
    } catch {
      // Ignore JSON parsing errors
    }

    throw new APIError(
      `Non-success status ${response.status} from ${response.url}`,
      response,
      responseBody
    );
  }

  return response.json();
}

export { appServerFetch }